export interface LiveProduct {
  item_id: string;
  item_name?: string | null;
  item_type: string;
  /** 0-based label from live_session_products.sort_order */
  sort_order?: number;
  pinned?: boolean;
  /** BOGO discount id when this product is a combo placeholder. */
  combo_discount_id?: string | null;
}

/** Combo placeholder products are bought through the combo picker, not a variant. */
export function comboDiscountId(p?: LiveProduct | null): string | null {
  return p?.combo_discount_id ? String(p.combo_discount_id) : null;
}

/** Compare Shopify / session product ids safely (string vs number). */
export function sameProductId(a?: string | number | null, b?: string | number | null): boolean {
  if (a == null || b == null || a === '' || b === '') return false;
  return String(a) === String(b);
}

/** Resolve the currently pinned / showcased product id for a session. */
export function resolvePinnedProductId(session: {
  showcased_product_id?: string | null;
  products?: Array<{ item_id: string; pinned?: boolean }>;
}): string | null {
  if (session.showcased_product_id != null && session.showcased_product_id !== '') {
    return String(session.showcased_product_id);
  }
  const pinned = (session.products || []).find((p) => p.pinned);
  return pinned ? String(pinned.item_id) : null;
}

export function findPinnedProduct(session: {
  showcased_product_id?: string | null;
  products?: LiveProduct[];
}): LiveProduct | null {
  const pinnedId = resolvePinnedProductId(session);
  if (!pinnedId) return null;
  const products = session.products || [];
  const found = products.find((p) => sameProductId(p.item_id, pinnedId));
  if (found) return found;
  // Showcase id is set but missing from products list — still show a card
  return {
    item_id: pinnedId,
    item_name: null,
    item_type: 'product',
    pinned: true,
  };
}

export interface LiveSessionDiscount {
  discount_id: string;
  discount_name?: string | null;
  discount_type?: string | null;
  discount_status?: string | null;
  live_exclusive?: boolean;
  display_name?: string | null;
  display_off?: string | null;
  display_min_spend?: string | null;
  duration_minutes?: number | null;
  usage_limit?: number | null;
  product_id?: string | null;
  product_ids?: string[];
  drop_started_at?: string | null;
  uses_count?: number;
  drop_ends_at?: string | null;
  uses_remaining?: number | null;
  available?: boolean;
  unavailable_reason?: 'inactive' | 'expired' | 'scheduled' | 'redeemed' | null;
}

export function isLiveVoucher(d: LiveSessionDiscount): boolean {
  return Boolean(d.live_exclusive && (d.discount_type === 'voucher' || !d.discount_type));
}

export function isStoreVoucher(d: LiveSessionDiscount): boolean {
  return !d.live_exclusive && d.discount_type === 'voucher';
}

export function assignedProductIds(d: {
  product_ids?: string[] | null;
  product_id?: string | null;
}): string[] {
  const list = Array.isArray(d.product_ids) ? d.product_ids : [];
  const cleaned = list.map((id) => String(id)).filter(Boolean);
  if (cleaned.length) return cleaned;
  return d.product_id ? [String(d.product_id)] : [];
}

function dropAppliesToProduct(d: LiveSessionDiscount, productId: string): boolean {
  const ids = assignedProductIds(d);
  if (!ids.length) return true;
  return ids.some((id) => sameProductId(id, productId));
}

function dropIsRunning(d: LiveSessionDiscount): boolean {
  if (!d.drop_started_at) return false;
  if (d.available === false) return false;
  if (d.discount_status && d.discount_status !== 'active') return false;
  return true;
}

/** Live drop currently running for this product (attached SKU wins over session-wide). */
export function pickHighlightLiveDrop(
  discounts: LiveSessionDiscount[] | undefined,
  productId: string
): LiveSessionDiscount | null {
  const drops = (discounts || []).filter(
    (d) => isLiveVoucher(d) && dropIsRunning(d) && dropAppliesToProduct(d, productId)
  );
  const attached = drops.filter((d) =>
    assignedProductIds(d).some((id) => sameProductId(id, productId))
  );
  const pool = attached.length ? attached : drops;
  return (
    pool.find((d) => d.duration_minutes != null && d.drop_ends_at) ||
    pool.find((d) => d.usage_limit != null) ||
    pool[0] ||
    null
  );
}

export function formatDropClock(remainingMs: number): string {
  const totalSec = Math.max(0, Math.floor(remainingMs / 1000));
  const hours = Math.floor(totalSec / 3600);
  const minutes = Math.floor((totalSec % 3600) / 60);
  const seconds = totalSec % 60;
  const mm = String(minutes).padStart(2, '0');
  const ss = String(seconds).padStart(2, '0');
  if (hours > 0) return `${hours}:${mm}:${ss}`;
  return `${mm}:${ss}`;
}

export interface LiveSessionPublic {
  id: string;
  title: string;
  status: string;
  facebook_live_url: string | null;
  facebook_video_id: string | null;
  showcased_product_id: string | null;
  viewer_count: number;
  products?: LiveProduct[];
  discounts?: LiveSessionDiscount[];
  social_proof?: {
    add_to_cart_last_5_min: number;
    product_views: Record<string, number>;
    viewer_count: number;
  };
  stripe_enabled?: boolean;
  stripe_publishable_key?: string | null;
}

export interface ChatComment {
  id: string;
  message: string;
  from: string;
  created_time?: string;
  source?: 'site' | 'facebook' | 'host';
}

export interface ShopifyCartLine {
  key: string;
  id: number;
  product_id: number;
  variant_id: number;
  title: string;
  quantity: number;
  price: number;
  line_price: number;
  image?: string | null;
  product_title?: string;
  auto_added?: boolean;
}

export function getAnonId(): string {
  const key = 'intan_live_anon_id';
  let id = localStorage.getItem(key);
  if (!id) {
    id = `anon_${Math.random().toString(36).slice(2)}_${Date.now()}`;
    localStorage.setItem(key, id);
  }
  return id;
}

export function getChatName(): string | null {
  return localStorage.getItem('intan_live_chat_name');
}

export function setChatName(name: string): void {
  localStorage.setItem('intan_live_chat_name', name.trim());
}

export function centsToCurrency(cents: number): number {
  return Math.round(cents) / 100;
}

export async function fetchShopifyCart(): Promise<any> {
  const res = await fetch('/cart.js', { credentials: 'same-origin' });
  if (!res.ok) throw new Error('Failed to load cart');
  return res.json();
}

export function mapCart(shopifyCart: any, liveSessionId?: string) {
  return {
    items: (shopifyCart.items || []).map((item: any) => ({
      key: item.key || `cart_item_${item.id}`,
      product_id: String(item.product_id),
      variant_id: String(item.variant_id),
      price: centsToCurrency(item.price),
      quantity: item.quantity,
      title: item.product_title || item.title,
      properties: item.properties || undefined,
      auto_added:
        item.properties?._auto_added == true || item.properties?._auto_added === 'true',
    })),
    ...(liveSessionId ? { live_session_id: liveSessionId } : {}),
  };
}

export function mapCartLines(shopifyCart: any): ShopifyCartLine[] {
  return (shopifyCart.items || []).map((item: any) => ({
    key: item.key,
    id: item.id,
    product_id: item.product_id,
    variant_id: item.variant_id,
    title: item.product_title || item.title,
    quantity: item.quantity,
    price: item.price,
    line_price: item.line_price,
    image: item.image || item.featured_image?.url || null,
    product_title: item.product_title,
    auto_added:
      item.properties?._auto_added == true || item.properties?._auto_added === 'true',
  }));
}

export interface DiscountCalculateResult {
  final_total?: number;
  updateCart?: Record<
    string,
    {
      quantity: number;
      auto_added: boolean;
    }
  >;
  [key: string]: unknown;
}

/**
 * Call discount calculator and apply `updateCart` to Shopify cart
 * (same pattern as shopify-bundle: /cart/update.js + _auto_added props).
 */
export async function calculateAndApplyCartUpdates(
  apiUrl: string,
  shopifyCart?: any,
  liveSessionId?: string
): Promise<{
  shopifyCart: any;
  finalTotal: number;
  calculation: DiscountCalculateResult | null;
}> {
  const currentCart = shopifyCart || (await fetchShopifyCart());
  const apiCart = mapCart(currentCart, liveSessionId);

  if (!apiCart.items.length) {
    return { shopifyCart: currentCart, finalTotal: 0, calculation: null };
  }

  const res = await fetch(`${apiUrl}/discounts/calculate`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ cart: apiCart }),
  });
  const data = await res.json();
  if (!res.ok || !data.success) {
    throw new Error(data.message || 'Failed to calculate discounts');
  }

  const calculation = (data.data || {}) as DiscountCalculateResult;

  if (!calculation.updateCart || Object.keys(calculation.updateCart).length === 0) {
    return {
      shopifyCart: currentCart,
      finalTotal:
        calculation.final_total != null
          ? Number(calculation.final_total)
          : centsToCurrency(currentCart.total_price || 0),
      calculation,
    };
  }

  const updates = new Map<string, number>();
  const variantToKey = new Map<number | string, string>();
  const autoAddedVariantIds = new Set<number | string>();

  (currentCart.items || []).forEach((item: any) => {
    const key = item.key || String(item.id);
    variantToKey.set(item.variant_id, key);
    updates.set(key, item.quantity);
  });

  Object.entries(calculation.updateCart).forEach(([rawKey, instruction]) => {
    let targetKey = rawKey;
    if (!updates.has(targetKey)) {
      const numericKey = Number(rawKey);
      if (!Number.isNaN(numericKey)) {
        const existingKey = variantToKey.get(numericKey);
        targetKey = existingKey || String(numericKey);
      }
    }
    updates.set(targetKey, instruction.quantity);

    if (instruction.auto_added) {
      const numericKey = Number(rawKey);
      if (!Number.isNaN(numericKey)) {
        autoAddedVariantIds.add(numericKey);
      } else {
        const variantMatch = Array.from(variantToKey.entries()).find(([, key]) => key === rawKey);
        if (variantMatch) autoAddedVariantIds.add(variantMatch[0]);
      }
    }
  });

  const formData = new FormData();
  updates.forEach((quantity, key) => {
    formData.append(`updates[${key}]`, String(quantity));
  });

  const updateResponse = await fetch('/cart/update.js', {
    method: 'POST',
    body: formData,
  });
  if (!updateResponse.ok) {
    const errorData = await updateResponse.json().catch(() => ({}));
    throw new Error(errorData.message || `Failed to update cart (${updateResponse.status})`);
  }

  const updatedCart = await updateResponse.json();

  const autoAddedKeys = new Set<string>();
  (updatedCart.items || []).forEach((item: any) => {
    const key = item.key || String(item.id);
    const entryByKey = calculation.updateCart![key];
    const entryByVariant = calculation.updateCart![String(item.variant_id)];
    if (
      entryByKey?.auto_added ||
      entryByVariant?.auto_added ||
      autoAddedVariantIds.has(item.variant_id)
    ) {
      autoAddedKeys.add(key);
    }
  });

  for (const key of autoAddedKeys) {
    const propFormData = new FormData();
    propFormData.append('id', key);
    propFormData.append('properties[_auto_added]', 'true');
    await fetch('/cart/change.js', {
      method: 'POST',
      body: propFormData,
    }).catch((err) => {
      console.error('Failed to update auto-added properties for key:', key, err);
    });
  }

  // Re-fetch so line properties (_auto_added) are current, then recalc total on final cart
  const finalCart = await fetchShopifyCart();
  const finalApiCart = mapCart(finalCart, liveSessionId);
  const finalRes = await fetch(`${apiUrl}/discounts/calculate`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ cart: finalApiCart }),
  });
  const finalData = await finalRes.json();
  const finalCalculation = (finalData?.data || calculation) as DiscountCalculateResult;

  return {
    shopifyCart: finalCart,
    finalTotal:
      finalCalculation.final_total != null
        ? Number(finalCalculation.final_total)
        : centsToCurrency(finalCart.total_price || 0),
    calculation: finalCalculation,
  };
}

export async function calculateFinalTotal(
  apiUrl: string,
  shopifyCart: any,
  liveSessionId?: string
): Promise<number> {
  const result = await calculateAndApplyCartUpdates(apiUrl, shopifyCart, liveSessionId);
  return result.finalTotal;
}

export function loadStripeJs(): Promise<any> {
  return new Promise((resolve, reject) => {
    if ((window as any).Stripe) {
      resolve((window as any).Stripe);
      return;
    }
    const script = document.createElement('script');
    script.src = 'https://js.stripe.com/v3/';
    script.async = true;
    script.onload = () => resolve((window as any).Stripe);
    script.onerror = () => reject(new Error('Failed to load Stripe.js'));
    document.head.appendChild(script);
  });
}

export const MINI_POS_KEY = 'intan_live_mini_pos';
export const MINI_COLLAPSED_KEY = 'intan_live_mini_collapsed';
export const MINI_W = 156;
export const MINI_H = Math.round((156 * 16) / 9);
